import { observer } from 'mobx-react';
import * as React from 'react';
import {
  AreaInput,
  Button,
  Card,
  DateInput,
  FormCell,
  Icon,
  IconSize,
  IconType,
  NumberInput,
  Select,
  TextInput,
  Grid,
  FlexRow,
  IDateInputValue,
} from 'ui-toolkit';
import { ISearchReportChange } from '../../services/SearchReportService';
import { deedDocumentOptions, estateOptions } from '../../documentTypes';
import { IDeedData } from '../../../ui-toolkit/orders/interfaces/titleReport/IDeed';
import { eEstateType } from '../../../ui-toolkit/orders/interfaces/titleReport/eEstateType';
import { eDeedDocumentType } from '../../../ui-toolkit/orders/interfaces/titleReport/eDeedDocumentType';

interface IProps {
  data: IDeedData[];
  error?: any;
  title?: string;
  onFormChange: (dataPath: string, newData: ISearchReportChange, error?: string | null | undefined) => void;
  onAddNewItem: (dataPath: string) => void;
  onDeleteItem: (dataPath: string, index: number) => void;
  validateFormData: (path: string) => void;
}

export const DeedInformation = observer(class DeedInformation extends React.Component<IProps, {}> {
  public static defaultProps: Partial<IProps> = {
    title: 'Deed Information',
  };

  public render() {
    const dataLen = this.props.data.length;

    const onAddDeed = () => this.props.onAddNewItem('deedInformation');
    return (
      <Card title={this.props.title}>
        <Grid>
          {this.props.data.map((deed: IDeedData, index: number) => {
            const onDeleteClick = () => this.props.onDeleteItem('deedInformation', index);
            const onGranteeChange = (value: string | null, error: string | null | undefined) =>
              this.onChange('grantee', index, value, error);
            const onGrantorChange = (value: string | null, error: string | null | undefined) =>
              this.onChange(`grantor`, index, value, error);
            const onEstateTypeChange = (value?: { value?: eEstateType }) =>
              this.onChange(`estateType`, index, value && value.value);
            const onDocumentTypeChange = (value?: { value?: eDeedDocumentType }) =>
              this.onDocumentTypeChange(index, value && value.value, null);
            const onConsiderationChange = (value: number | null | undefined) =>
              this.onChange(`consideration`, index, value);
            const onDeedDateChange = (value?: IDateInputValue) =>
              this.onChange(`deedDate`, index, value ? value.value : null);
            const onRecordedDateChange = (value?: IDateInputValue) =>
              this.onChange(`recordedDate`, index, value ? value.value : null);
            const onOtherDocumentTypeChange = (value: string | null | undefined, error: string | null | undefined) =>
              this.onChange('otherDocumentType', index, value, error);
            const onOtherDocumentTypeBlur = () =>
              this.props.validateFormData(`deedInformation[${index}].otherDocumentType`);
            const onInstrumentNumberChange = (value: string | null | undefined, error: string | null | undefined) =>
              this.onChange(`instrumentNumber`, index, value, error);
            const onBookChange = (value: string | null | undefined, error: string | null | undefined) =>
              this.onChange(`book`, index, value, error);
            const onAdditionalInformationChange = (
              value: string | null | undefined,
              error: string | null | undefined
            ) => this.onChange(`additionalInformation`, index, value, error);
            return (
              <Grid key={`deed-information-${index}`}>
                <FlexRow justifyContent={'space-between'}>
                  <h3>Deed No {index + 1}</h3>
                  <Button intent="ghost" isInline onClick={onDeleteClick} className="delete-button">
                    <Icon type={IconType.Remove} size={IconSize.Small} />
                  </Button>
                </FlexRow>

                <Grid columnCount={2}>
                  <TextInput
                    value={deed.grantee}
                    errorText={this.props.error[`deedInformation[${index}].grantee`]}
                    label="Grantee(s)"
                    placeholder="ex. Beyonce Knowles, a married women"
                    className="card--row-element"
                    onChange={onGranteeChange}
                  />
                  <TextInput
                    value={deed.grantor}
                    errorText={this.props.error[`deedInformation[${index}].grantor`]}
                    label="Grantor(s)"
                    placeholder="ex. Sean Carter"
                    className="card--row-element"
                    onChange={onGrantorChange}
                  />
                </Grid>
                <Grid columnCount={this.props.data[index].documentType === 'other' ? 3 : 2}>
                  <FormCell
                    errorText={this.props.error[`deedInformation[${index}].documentType`]}
                    label="Document type"
                  >
                    <Select
                      isClearable={true}
                      value={deedDocumentOptions.filter((opt) => opt.value === deed.documentType)}
                      placeholder="Select"
                      options={deedDocumentOptions}
                      onChange={onDocumentTypeChange}
                    />
                  </FormCell>
                  {this.props.data[index].documentType === 'other' ? (
                    <TextInput
                      value={deed.otherDocumentType}
                      errorText={this.props.error[`deedInformation[${index}].other_document_type`]}
                      label="Other document type"
                      isRequired={this.props.data[index].documentType === 'other'}
                      placeholder="Add document type name"
                      onChange={onOtherDocumentTypeChange}
                      onBlur={onOtherDocumentTypeBlur}
                    />
                  ) : null}
                  <FormCell errorText={this.props.error[`deedInformation[${index}].estateType`]} label="Estate type">
                    <Select
                      isClearable={true}
                      value={estateOptions.filter((opt) => opt.value === deed.estateType)}
                      placeholder="Select"
                      options={estateOptions}
                      onChange={onEstateTypeChange}
                    />
                  </FormCell>
                </Grid>
                <Grid columnCount={2}>
                  <NumberInput
                    value={deed.consideration}
                    label="Consideration"
                    prefix="$"
                    decimalScale={2}
                    placeholder="XXX,XXX,XXX.XX"
                    onChange={onConsiderationChange}
                    mode="currency"
                    thousandSeparator={true}
                  />
                  <Grid columnCount={2}>
                    <DateInput
                      value={deed.deedDate || null}
                      errorText={this.props.error[`deedInformation[${index}].deedDate`]}
                      label="Deed date"
                      onChange={onDeedDateChange}
                    />
                    <DateInput
                      value={deed.recordedDate || null}
                      errorText={this.props.error[`deedInformation[${index}].recordedDate`]}
                      label="Recorded date"
                      onChange={onRecordedDateChange}
                    />
                  </Grid>
                </Grid>
                <Grid columnCount={2}>
                  <Grid columnCount={2}>
                    <TextInput
                      value={deed.instrumentNumber}
                      label="Instrument No."
                      placeholder="XXXXXXXXXX"
                      onChange={onInstrumentNumberChange}
                    />
                    <TextInput value={deed.book} label="Book/page" placeholder="XXXX" onChange={onBookChange} />
                  </Grid>

                  <AreaInput
                    value={deed.additionalInformation}
                    label="Additional information"
                    rows={1}
                    placeholder="Please include additional information here"
                    onChange={onAdditionalInformationChange}
                  />
                </Grid>
                {index !== dataLen - 1 && <hr className="card-horizontal-separator" />}
              </Grid>
            );
          })}
          <FlexRow>
            <Button intent="secondary" onClick={onAddDeed}>
              Another deed
            </Button>
          </FlexRow>
        </Grid>
      </Card>
    );
  }

  private onDocumentTypeChange = (
    index: number,
    value: string | null | undefined,
    error: string | null | undefined
  ) => {
    this.onChange(`documentType`, index, value, error);
    if (value !== 'other') {
      this.onChange(`otherDocumentType`, index, null, null);
    }
  };

  private onChange = (itemDataPath: string, itemIndex: number, newValue: any, error?: string | null | undefined) => {
    this.props.onFormChange(
      'deedInformation',
      {
        data: newValue,
        dataPath: itemDataPath,
        index: itemIndex,
      },
      error
    );
  };
});
